import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, LogIn, ArrowRight, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { queryClient } from '@/lib/queryClient';

/**
 * Visas när en BankID-inloggning skapat ett HELT NYTT konto som varken har
 * inbjudningar eller åtkomster. Vanligaste orsaken: personen är inbjuden via
 * e-post eller telefon (inte personnummer) och behöver logga in med den
 * kanalen i stället — utan denna sida möts hen bara av en tom app.
 */
export default function BankIdWelcomePage() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [switching, setSwitching] = useState(false);

  const handleSwitchLogin = async () => {
    setSwitching(true);
    try {
      await signOut();
    } catch {
      // Utloggning misslyckades — navigera ändå; auth-sidan hanterar resten
    }
    queryClient.clear();
    navigate('/auth', { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-6">
      <Card className="rounded-3xl w-full max-w-md">
        <CardContent className="p-6 space-y-5">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--foreground)/0.08)]">
            <KeyRound className="h-6 w-6 text-foreground" />
          </div>

          <div className="space-y-2">
            <h1 className="text-xl font-semibold tracking-tight">
              Ditt BankID är inte kopplat till någon åtkomst ännu
            </h1>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Har du fått en inbjudan via e-post eller SMS? Då är den knuten
              till din e-postadress eller ditt telefonnummer — logga in med den
              metoden i stället. Därifrån kan du sedan koppla BankID till ditt
              konto under Profil.
            </p>
          </div>

          <div className="space-y-2">
            <Button className="w-full" size="lg" onClick={handleSwitchLogin} disabled={switching}>
              {switching ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <LogIn className="h-5 w-5" />
                  Logga in med e-post eller telefon
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => navigate('/keys', { replace: true })}
              disabled={switching}
            >
              Fortsätt ändå
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            Väntar du på en inbjudan till just detta BankID dyker den upp
            automatiskt nästa gång du öppnar appen.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
